import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, Loader2, Wand2, FileText } from "lucide-react";

const TXN_TYPES = [
  { value: "buying", label: "Buying" },
  { value: "selling", label: "Selling" },
  { value: "both", label: "Buying & Selling" },
  { value: "remortgage", label: "Remortgage" },
  { value: "transfer", label: "Transfer of Equity" },
  { value: "grant_of_lease", label: "Grant of Lease" },
];

const LEGAL_PRESET_KEYS = [
  ["legal_fee_buying", "Solicitor's Fee (Buying)"],
  ["legal_fee_selling", "Solicitor's Fee (Selling)"],
  ["legal_fee_both", "Solicitor's Fee (Buying & Selling)"],
  ["legal_fee_remortgage", "Solicitor's Fee (Remortgage)"],
  ["legal_fee_transfer", "Solicitor's Fee (Transfer of Equity)"],
  ["legal_fee_grant_of_lease", "Solicitor's Fee (Grant of Lease)"],
  ["lender_fee", "Mortgage Lender Legal Fee"],
  ["leasehold_fee", "Leasehold Fee"],
  ["new_build_supplement", "New Build Supplement"],
  ["shared_ownership_supplement", "Shared Ownership Supplement"],
  ["gifted_deposit_fee_per_giftor", "Gifted Deposit (per giftor)"],
  ["htb_isa_fee", "Help to Buy ISA Fee"],
  ["lifetime_isa_fee", "Lifetime ISA Fee"],
  ["ltd_company_supplement", "Ltd Company Supplement"],
  ["auction_supplement", "Auction Supplement"],
  ["right_to_buy_supplement", "Right to Buy Supplement"],
  ["bridging_finance_supplement", "Bridging Finance Supplement"],
  ["commercial_supplement", "Commercial Element Supplement"],
  ["bsa_fee", "BSA Supplement"],
  ["mortgage_redemption_fee", "Mortgage Redemption Fee"],
  ["htb_loan_redemption_fee", "Help to Buy Loan Redemption"],
];

const DISB_PRESET_KEYS = [
  ["land_registry_search", "Land Registry Search"],
  ["bankruptcy_search", "Bankruptcy Search (per name)"],
  ["bank_transfer_fee", "Bank Transfer Fee"],
];

const money = (n) => `£${(Number(n) || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const emptyRow = () => ({ name: "", amount: "" });

export default function CustomQuoteBuilder() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sdlt, setSdlt] = useState({ total: 0 });
  const [form, setForm] = useState({
    client_first_name: "", client_middle_name: "", client_surname: "",
    client_email: "", client_phone: "",
    property_value: "", property_address: "", property_postcode: "",
    transaction_type: "buying", property_type: "freehold",
    first_time_buyer: false, second_home: false, ltd_company: false, commercial: false,
    additional_notes: "",
  });
  const [legalItems, setLegalItems] = useState([emptyRow()]);
  const [disbItems, setDisbItems] = useState([emptyRow()]);

  useEffect(() => {
    API.get("/quote-settings").then(({ data }) => setSettings(data)).catch(() => setSettings({}));
  }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const sdltApplicable = ["buying", "both", "grant_of_lease"].includes(form.transaction_type);

  // Live SDLT preview whenever the value / flags / txn type change.
  const refreshSdlt = useCallback(async () => {
    const val = parseFloat(form.property_value) || 0;
    if (!sdltApplicable || val <= 0) { setSdlt({ total: 0 }); return; }
    try {
      const { data } = await API.get("/sdlt-preview", {
        params: {
          property_value: val,
          first_time_buyer: form.first_time_buyer,
          second_home: form.second_home,
          ltd_company: form.ltd_company,
          commercial: form.commercial,
          transaction_type: form.transaction_type,
        },
      });
      setSdlt(data || { total: 0 });
    } catch { setSdlt({ total: 0 }); }
  }, [form.property_value, form.first_time_buyer, form.second_home, form.ltd_company, form.commercial, form.transaction_type, sdltApplicable]);

  useEffect(() => {
    const t = setTimeout(refreshSdlt, 350);
    return () => clearTimeout(t);
  }, [refreshSdlt]);

  const legalPresetOptions = useMemo(() => {
    if (!settings) return [];
    const opts = [];
    LEGAL_PRESET_KEYS.forEach(([key, label]) => {
      const amt = settings[key];
      if (typeof amt === "number" && amt > 0) opts.push({ id: `k:${key}`, name: label, amount: amt });
    });
    (settings.custom_fees || []).filter((cf) => ["legal", "supplementary"].includes(cf.category))
      .forEach((cf) => opts.push({ id: `c:${cf.id}`, name: cf.name, amount: cf.amount }));
    return opts;
  }, [settings]);

  const disbPresetOptions = useMemo(() => {
    if (!settings) return [];
    const opts = [];
    DISB_PRESET_KEYS.forEach(([key, label]) => {
      const amt = settings[key];
      if (typeof amt === "number" && amt > 0) opts.push({ id: `k:${key}`, name: label, amount: amt });
    });
    (settings.custom_fees || []).filter((cf) => cf.category === "disbursement")
      .forEach((cf) => opts.push({ id: `c:${cf.id}`, name: cf.name, amount: cf.amount }));
    return opts;
  }, [settings]);

  const addPreset = (which, optId) => {
    const opts = which === "legal" ? legalPresetOptions : disbPresetOptions;
    const opt = opts.find((o) => o.id === optId);
    if (!opt) return;
    const row = { name: opt.name, amount: String(opt.amount) };
    if (which === "legal") setLegalItems((p) => [...p.filter((r) => r.name || r.amount), row]);
    else setDisbItems((p) => [...p.filter((r) => r.name || r.amount), row]);
  };

  const updateRow = (which, idx, key, value) => {
    const setter = which === "legal" ? setLegalItems : setDisbItems;
    setter((p) => p.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));
  };
  const removeRow = (which, idx) => {
    const setter = which === "legal" ? setLegalItems : setDisbItems;
    setter((p) => (p.length <= 1 ? [emptyRow()] : p.filter((_, i) => i !== idx)));
  };
  const addRow = (which) => {
    const setter = which === "legal" ? setLegalItems : setDisbItems;
    setter((p) => [...p, emptyRow()]);
  };

  const legalTotal = useMemo(() => legalItems.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0), [legalItems]);
  const vat = useMemo(() => Math.round(legalTotal * 0.2 * 100) / 100, [legalTotal]);
  const disbTotal = useMemo(() => disbItems.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0), [disbItems]);
  const grandTotal = legalTotal + vat + disbTotal + (sdlt.total || 0);

  const submit = async () => {
    const name = [form.client_first_name, form.client_surname].map((x) => x.trim()).filter(Boolean).join(" ");
    if (!name) { toast.error("Enter the client's first name and surname."); return; }
    if (!(parseFloat(form.property_value) > 0) && sdltApplicable) { toast.error("Enter a property value."); return; }
    const legal = legalItems.filter((r) => r.name.trim()).map((r) => ({ name: r.name.trim(), amount: parseFloat(r.amount) || 0 }));
    const disb = disbItems.filter((r) => r.name.trim()).map((r) => ({ name: r.name.trim(), amount: parseFloat(r.amount) || 0 }));
    if (!legal.length) { toast.error("Add at least one legal fee line."); return; }
    setSaving(true);
    try {
      const { data } = await API.post("/quotes/custom", {
        ...form,
        property_value: parseFloat(form.property_value) || 0,
        legal_items: legal,
        disbursements: disb,
      });
      toast.success("Custom quote created.");
      navigate(`/quote/view/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create the quote.");
    } finally { setSaving(false); }
  };

  const rowBlock = (which, items, presetOptions) => (
    <div className="space-y-2">
      {items.map((r, i) => (
        <div key={i} className="flex items-center gap-2" data-testid={`${which}-row-${i}`}>
          <Input
            value={r.name}
            onChange={(e) => updateRow(which, i, "name", e.target.value)}
            placeholder={which === "legal" ? "Fee description (e.g. Solicitor's fee)" : "Disbursement description"}
            className="flex-1"
            data-testid={`${which}-name-${i}`}
          />
          <div className="relative w-36">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">£</span>
            <Input
              type="number" min="0" step="0.01"
              value={r.amount}
              onChange={(e) => updateRow(which, i, "amount", e.target.value)}
              placeholder="0.00"
              className="pl-6 text-right"
              data-testid={`${which}-amount-${i}`}
            />
          </div>
          <button onClick={() => removeRow(which, i)} className="text-slate-400 hover:text-rose-600 p-1.5" data-testid={`${which}-remove-${i}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button type="button" variant="outline" size="sm" onClick={() => addRow(which)} className="h-8 text-xs gap-1.5" data-testid={`${which}-add-row`}>
          <Plus className="w-3.5 h-3.5" /> Add line
        </Button>
        {presetOptions.length > 0 && (
          <Select onValueChange={(v) => addPreset(which, v)} value="">
            <SelectTrigger className="h-8 text-xs w-auto gap-1.5" data-testid={`${which}-preset-select`}>
              <Wand2 className="w-3.5 h-3.5 text-indigo-500" />
              <SelectValue placeholder="Add from presets" />
            </SelectTrigger>
            <SelectContent>
              {presetOptions.map((o) => (
                <SelectItem key={o.id} value={o.id} data-testid={`${which}-preset-${o.id}`}>
                  {o.name} — {money(o.amount)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="custom-quote-builder">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <button onClick={() => navigate("/quotes")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="back-to-quotes">
          <ArrowLeft className="w-4 h-4" /> Back to quotes
        </button>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[#100f5d] text-white flex items-center justify-center"><FileText className="w-5 h-5" /></div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>Custom quote</h1>
            <p className="text-sm text-slate-500">Type your own fees, or pull from preset fees — disbursements are editable and SDLT is calculated automatically.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5">
            {/* Client */}
            <section className="bg-white border border-slate-200 rounded-xl p-5">
              <h2 className="text-sm font-semibold text-slate-900 mb-3">Client</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Input value={form.client_first_name} onChange={(e) => set("client_first_name", e.target.value)} placeholder="First name" data-testid="cq-first-name" />
                <Input value={form.client_middle_name} onChange={(e) => set("client_middle_name", e.target.value)} placeholder="Middle name(s)" data-testid="cq-middle-name" />
                <Input value={form.client_surname} onChange={(e) => set("client_surname", e.target.value)} placeholder="Surname" data-testid="cq-surname" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                <Input value={form.client_email} onChange={(e) => set("client_email", e.target.value)} placeholder="Email" data-testid="cq-email" />
                <Input value={form.client_phone} onChange={(e) => set("client_phone", e.target.value)} placeholder="Phone" data-testid="cq-phone" />
              </div>
            </section>

            {/* Property */}
            <section className="bg-white border border-slate-200 rounded-xl p-5">
              <h2 className="text-sm font-semibold text-slate-900 mb-3">Property & matter</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Select value={form.transaction_type} onValueChange={(v) => set("transaction_type", v)}>
                  <SelectTrigger data-testid="cq-txn-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TXN_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
                  <SelectTrigger data-testid="cq-prop-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="freehold">Freehold</SelectItem>
                    <SelectItem value="leasehold">Leasehold</SelectItem>
                  </SelectContent>
                </Select>
                <div className="relative sm:col-span-2">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">£</span>
                  <Input type="number" min="0" value={form.property_value} onChange={(e) => set("property_value", e.target.value)} placeholder="Property value" className="pl-6" data-testid="cq-property-value" />
                </div>
                <Input value={form.property_address} onChange={(e) => set("property_address", e.target.value)} placeholder="Property address" data-testid="cq-property-address" />
                <Input value={form.property_postcode} onChange={(e) => set("property_postcode", e.target.value)} placeholder="Postcode" data-testid="cq-property-postcode" />
              </div>
              {sdltApplicable && (
                <div className="flex flex-wrap gap-4 mt-3 text-sm">
                  {[["first_time_buyer", "First-time buyer"], ["second_home", "Second home / BTL"], ["ltd_company", "Ltd company"], ["commercial", "Commercial / mixed-use"]].map(([k, l]) => (
                    <label key={k} className="inline-flex items-center gap-2 cursor-pointer" data-testid={`cq-flag-${k}`}>
                      <input type="checkbox" checked={form[k]} onChange={(e) => set(k, e.target.checked)} className="rounded border-slate-300" />
                      <span className="text-slate-700">{l}</span>
                    </label>
                  ))}
                </div>
              )}
            </section>

            {/* Legal fees */}
            <section className="bg-white border border-slate-200 rounded-xl p-5">
              <h2 className="text-sm font-semibold text-slate-900 mb-1">Legal fees <span className="text-slate-400 font-normal">(VAT added at 20%)</span></h2>
              <p className="text-xs text-slate-500 mb-3">Type each fee, or pull a preset from the dropdown and tweak the amount.</p>
              {rowBlock("legal", legalItems, legalPresetOptions)}
            </section>

            {/* Disbursements */}
            <section className="bg-white border border-slate-200 rounded-xl p-5">
              <h2 className="text-sm font-semibold text-slate-900 mb-1">Disbursements <span className="text-slate-400 font-normal">(no VAT)</span></h2>
              <p className="text-xs text-slate-500 mb-3">Add your own, or pull from presets.</p>
              {rowBlock("disb", disbItems, disbPresetOptions)}
            </section>

            {/* Notes */}
            <section className="bg-white border border-slate-200 rounded-xl p-5">
              <h2 className="text-sm font-semibold text-slate-900 mb-3">Notes (optional)</h2>
              <textarea value={form.additional_notes} onChange={(e) => set("additional_notes", e.target.value)} rows={3}
                placeholder="Anything to show on the quote…" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="cq-notes" />
            </section>
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-slate-200 rounded-xl p-5 sticky top-6" data-testid="cq-summary">
              <h2 className="text-sm font-semibold text-slate-900 mb-4">Quote summary</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-600">Legal fees</span><span className="font-medium" data-testid="cq-legal-total">{money(legalTotal)}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">VAT (20%)</span><span className="font-medium" data-testid="cq-vat">{money(vat)}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Disbursements</span><span className="font-medium" data-testid="cq-disb-total">{money(disbTotal)}</span></div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Stamp Duty (SDLT)</span>
                  <span className="font-medium" data-testid="cq-sdlt">{sdltApplicable ? money(sdlt.total || 0) : "N/A"}</span>
                </div>
                <hr className="border-slate-100 my-2" />
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-slate-900">Total</span>
                  <span className="text-2xl font-bold text-[#100f5d]" data-testid="cq-grand-total" style={{ fontFamily: "Outfit, sans-serif" }}>{money(grandTotal)}</span>
                </div>
              </div>
              <Button onClick={submit} disabled={saving} className="w-full mt-5 h-10 bg-[#100f5d] hover:bg-[#1a1980] text-white gap-2" data-testid="cq-create-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                Create quote
              </Button>
              <p className="text-[11px] text-slate-400 mt-2 text-center">Produces the same client-facing quote as normal.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
